import { useStripe } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Skeleton from 'react-loading-skeleton';

import {
  tikTokAdvancedMatching,
  tikTokEvent,
  twitterEvent,
  useAnalytics,
} from '@features/Analytics';
import {
  getFacebookPurchaseCustomData,
  getFacebookPurchaseParameters,
  getFacebookSubscribeCustomData,
} from '@features/Analytics/services/facebook-ltv';
import { createAppLovinEcommercePayload } from '@features/Analytics/utils';
import {
  processingModalStatusAtom,
  selectedPlanAtom,
  stripeSubscriptionWithPersistence,
  userAtom,
  userPaymentsInfoAtom,
} from '@features/Stripe/atoms';
import { CURRENCY_DOES_NOT_SUPPORT_PAYPAL } from '@features/Stripe/constants';
import {
  useCreateSubscription,
  useGetStripeSubscriptions,
  usePaymentRequest,
} from '@features/Stripe/hooks';
import { useGetStripeProductId } from '@features/Stripe/hooks/query/use-get-stripe-product-id';
import { CompactSelector } from '@features/Stripe/widgets/checkout-selector/widgets/compact-selector';
import { PaymentOptionTab } from '@features/Stripe/widgets/checkout-selector/widgets/payment-option-tab';
import type { TpStripeVariants } from 'src/constants/variants';
import { useFeature, useFeatures } from 'src/features/FeatureToggle';
import { getDataFromSessionStorage } from 'src/utils';
import { useGetAppName } from 'src/utils/hooks';

import AmexLogo from 'src/assets/icons/amex.svg';
import ApplePayLogo from 'src/assets/images/payment/apple-pay-logo.svg';
import MasterCard from 'src/assets/images/payment/mastercard-logo.svg';
import PayPalLogo from 'src/assets/images/payment/paypal-logo.svg';
import UnionPayLogo from 'src/assets/images/payment/unionpay-logo.svg';
import VisaLogo from 'src/assets/images/payment/visa-logo.svg';
import GooglePayLogo from '../../../../../public/images/gpay-logo-white-theme.svg';

import { useBuySecondSubscription } from '@features/Stripe/hooks/mutation/use-buy-second-subscription';
import styles from './checkout-selector.module.scss';

type TpPaymentOptions = {
  paymentMethodName: 'PayPal' | 'Google Pay' | 'Apple Pay' | 'Credit card';
  paymentMethodLogo: ReactNode;
  paymentMethodLogoCompact: ReactNode;
  paymentMethodContentList: string[] | undefined;
  paymentButtonText: string | undefined;
};

const PAYMENT_OPTIONS: Map<
  TpPaymentOptions['paymentMethodName'],
  TpPaymentOptions
> = new Map([
  [
    'Credit card',
    {
      paymentMethodName: 'Credit card',
      paymentMethodLogo: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gridGap: '8px',
            width: '146px',
            height: '18px',
          }}
        >
          <VisaLogo width={45.94} height={18} />
          <MasterCard width={23.4} height={18} />
          <AmexLogo width={25.2} height={18} />
          <UnionPayLogo width={27.9} height={18} />
        </div>
      ),
      paymentMethodLogoCompact: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="18"
          viewBox="0 0 21 18"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.7501 4.7898V10.0298C20.7501 11.4898 20.3601 12.5598 19.6701 13.2898C19.4901 13.4698 19.2201 13.3298 19.2201 13.0798V5.9098C19.2201 3.6598 17.4001 1.8398 15.1501 1.8398H4.28011C4.02011 1.8398 3.88011 1.5598 4.06011 1.3898C4.40011 1.0598 4.82011 0.809805 5.29011 0.639805C5.89011 0.409805 6.57011 0.299805 7.33011 0.299805H16.2601C18.9601 0.299805 20.7501 1.6598 20.7501 4.7898ZM13.97 3.25H4.53C4.25 3.25 3.99 3.26 3.75 3.27C1.38 3.41 0.75 4.28 0.75 6.98V7.56C0.75 8.11 1.2 8.56 1.75 8.56H16.75C17.3 8.56 17.75 8.11 17.75 7.56V6.98C17.75 4 16.99 3.25 13.97 3.25ZM1.75 10.0605C1.2 10.0605 0.75 10.5105 0.75 11.0605V13.9705C0.75 16.9505 1.51 17.7005 4.53 17.7005H13.97C16.94 17.7005 17.72 16.9805 17.75 14.1305V11.0605C17.75 10.5105 17.3 10.0605 16.75 10.0605H1.75ZM5.71 15.2605H4C3.59 15.2605 3.25 14.9205 3.25 14.5105C3.25 14.1005 3.59 13.7605 4 13.7605H5.72C6.13 13.7605 6.47 14.1005 6.47 14.5105C6.47 14.9205 6.13 15.2605 5.71 15.2605ZM11.3 15.2605H7.86C7.45 15.2605 7.11 14.9205 7.11 14.5105C7.11 14.1005 7.45 13.7605 7.86 13.7605H11.3C11.71 13.7605 12.05 14.1005 12.05 14.5105C12.05 14.9205 11.72 15.2605 11.3 15.2605Z"
            fill="#302C28"
          />
        </svg>
      ),
      paymentMethodContentList: undefined,
      paymentButtonText: 'Continue',
    },
  ],
  [
    'PayPal',
    {
      paymentMethodName: 'PayPal',
      paymentMethodLogo: <PayPalLogo />,
      paymentMethodLogoCompact: <PayPalLogo />,
      paymentMethodContentList: [
        'Fast, convenient payment option',
        'Keeps your financial info safe with end-to-end encryption',
        "Backed by PayPal's industry-leading fraud protection",
      ],
      paymentButtonText: 'Confirm purchase',
    },
  ],
  [
    'Apple Pay',
    {
      paymentMethodName: 'Apple Pay',
      paymentMethodLogo: <ApplePayLogo />,
      paymentMethodLogoCompact: <ApplePayLogo />,
      paymentMethodContentList: [
        'Fast, convenient payment option',
        'Keeps your financial info safe with end-to-end encryption',
      ],
      paymentButtonText: undefined,
    },
  ],
  [
    'Google Pay',
    {
      paymentMethodName: 'Google Pay',
      paymentMethodLogo: <GooglePayLogo />,
      paymentMethodLogoCompact: <GooglePayLogo />,
      paymentMethodContentList: [
        'Fast, convenient payment option',
        'Keeps your financial info safe with end-to-end encryption',
      ],
      paymentButtonText: undefined,
    },
  ],
]);

const SkeletonMethods = () => (
  <>
    <Skeleton count={1} height={350} />
    <Skeleton count={1} height={50} />
    <Skeleton count={1} height={50} />
  </>
);

const SkeletonMethodsCompact = () => (
  <>
    <Skeleton
      containerClassName={styles.containerSkeleton}
      count={3}
      height={48}
      className={styles.skeletonItem}
    />
    <Skeleton
      count={1}
      height={64}
      className={styles.skeletonItemSubmitButton}
    />
  </>
);

const APPLE_PAY = 'Apple Pay';
const GOOGLE_PAY = 'Google Pay';
const PAYPAL = 'PayPal';

const isPaymentMethodFiltered = (
  methodName: string,
  selectedPaymentMethod: string,
  isPayPalAvailable: boolean,
): boolean => {
  if (
    (selectedPaymentMethod === 'applePay' && methodName === GOOGLE_PAY) ||
    (selectedPaymentMethod === 'googlePay' && methodName === APPLE_PAY) ||
    (selectedPaymentMethod === '' &&
      (methodName === GOOGLE_PAY || methodName === APPLE_PAY))
  ) {
    return true;
  }
  return (
    (!isPayPalAvailable ||
      getDataFromSessionStorage({
        store: 'authStore',
        key: 'variant',
      }) === 'variant19') &&
    methodName === PAYPAL
  );
};

const getFilteredPaymentOptions = (
  selectedPaymentMethod: string,
  isPayPalAvailable: boolean,
  priorityMethod?: TpPaymentOptions['paymentMethodName'],
): TpPaymentOptions[] => {
  const filteredOptions: TpPaymentOptions[] = [];
  let priorityOption: TpPaymentOptions | undefined;

  for (const [methodName, option] of PAYMENT_OPTIONS) {
    if (
      isPaymentMethodFiltered(
        methodName,
        selectedPaymentMethod,
        isPayPalAvailable,
      )
    ) {
      continue;
    }

    if (priorityMethod === methodName) {
      priorityOption = option;
    }

    filteredOptions.push(option);
  }

  if (priorityOption) {
    filteredOptions.sort((a, b) =>
      a.paymentMethodName === priorityOption?.paymentMethodName
        ? -1
        : b.paymentMethodName === priorityOption?.paymentMethodName
          ? 1
          : 0,
    );
  }

  return filteredOptions;
};

export const CheckoutSelector = memo(
  ({
    isCompactStyle = false,
    priorityMethod,
    isOnlyPaymentRequestButton,
    onSuccessPayment,
    cancelSubscriptionAtPeriodEnd,
  }: {
    isCompactStyle?: boolean;
    isOnlyPaymentRequestButton?: boolean;
    priorityMethod?: TpPaymentOptions['paymentMethodName'];
    cancelSubscriptionAtPeriodEnd?: boolean;
    onSuccessPayment?: ({
      paymentMethod,
      paymentSystem,
    }: {
      paymentMethod: string;
      paymentSystem?: 'stripe' | 'braintree';
    }) => Promise<void>;
  }) => {
    const { email, token, userId, campaign, customerId } =
      useAtomValue(userAtom);
    const planInfo = useAtomValue(selectedPlanAtom);
    const setCustomerId = useSetAtom(userAtom);
    const stripe = useStripe();

    const [paymentInfoAtom, setPaymentInfoAtom] = useAtom(userPaymentsInfoAtom);
    const {
      introOffer: isIntroOfferEnabled,
      isBundleFeature: isBundleFeatureEnabled,
    } = useFeatures();
    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');

    const currentPlanDetails = useMemo(
      () =>
        isIntroOfferEnabled
          ? {
              fullPrice: planInfo.introOfferData?.fullPrice,
              fullPriceDiscount: planInfo.introOfferData?.fullPriceDiscount,
              introOfferId: planInfo.introOfferData?.introOfferId,
              introOfferDuration: Number(
                planInfo.introOfferData?.introOfferDuration ?? 0,
              ),
              billingPeriod: planInfo.introOfferData?.billingPeriod,
              billingPeriodUnit: planInfo.introOfferData?.billingPeriodUnit,
              currency: planInfo.introOfferData?.currency || planInfo.currency,
            }
          : {
              fullPrice: planInfo.fullPrice,
              fullPriceDiscount: planInfo.fullPriceDiscount,
              introOfferId: undefined,
              introOfferDuration: 0,
              billingPeriod: planInfo.billingPeriod,
              billingPeriodUnit: planInfo.billingPeriodUnit,
              currency: planInfo.currency,
            },
      [
        isIntroOfferEnabled,
        planInfo.introOfferData,
        planInfo.fullPrice,
        planInfo.fullPriceDiscount,
        planInfo.billingPeriod,
        planInfo.billingPeriodUnit,
        planInfo.currency,
      ],
    );

    const paymentRequestPlanInfo = useMemo(
      () => ({
        priceId: planInfo.priceId,
        planName: planInfo.planName,
        fullPriceDiscount: Number(currentPlanDetails.fullPriceDiscount),
        currency: currentPlanDetails.currency,
      }),
      [
        currentPlanDetails.currency,
        currentPlanDetails.fullPriceDiscount,
        planInfo.planName,
        planInfo.priceId,
      ],
    );

    const app = useGetAppName();

    const { isLoading, data, error } = useCreateSubscription({
      priceId: planInfo.priceId as string,
      currency: currentPlanDetails.currency,
      token,
      email,
      campaign,
      userId,
      app,
      introOfferId: currentPlanDetails.introOfferId,
      introOfferDuration: currentPlanDetails.introOfferDuration,
      cancelAtPeriodEnd: cancelSubscriptionAtPeriodEnd,
    });

    const { paymentMethodResult } = usePaymentRequest({
      stripe,
      planInfo: paymentRequestPlanInfo,
    });

    const isPayPalAvailable = useMemo(() => {
      const currencySupported = !CURRENCY_DOES_NOT_SUPPORT_PAYPAL.includes(
        currentPlanDetails.currency,
      );
      const hasBraintreeIds =
        (planInfo.braintreePlanId?.length ?? 0) > 0 &&
        (planInfo.braintreeMerchantId?.length ?? 0) > 0;

      return currencySupported && hasBraintreeIds && !isBundleFeatureEnabled;
    }, [
      currentPlanDetails.currency,
      planInfo.braintreePlanId,
      planInfo.braintreeMerchantId,
      isBundleFeatureEnabled,
    ]);

    const filteredPaymentOptions = useMemo(
      () =>
        getFilteredPaymentOptions(
          paymentMethodResult ?? '',
          isPayPalAvailable,
          priorityMethod as TpPaymentOptions['paymentMethodName'],
        ),
      [paymentMethodResult, isPayPalAvailable, priorityMethod],
    );

    const [clientSecret, setClientSecret] = useState('');
    const [activePaymentMethod, setActivePaymentMethod] = useState(
      filteredPaymentOptions[0]?.paymentMethodName ?? '',
    );

    const handlePaymentMethodSelect = useCallback(
      (paymentMethodName: TpPaymentOptions['paymentMethodName']) =>
        setActivePaymentMethod(paymentMethodName),
      [],
    );

    const { predictedLtv, productId } = useGetStripeProductId({
      variant: getDataFromSessionStorage({
        store: 'authStore',
        key: 'variant',
      }) as TpStripeVariants,
    });

    useEffect(() => {
      if (isLoading || error || !data) {
        setClientSecret('');
        return;
      }

      const { client_secret, customer_id, subscription_id } = data;
      setCustomerId((prev) => ({ ...prev, customerId: customer_id }));
      setPaymentInfoAtom((prev) => ({
        ...prev,
        subscriptionId: subscription_id,
      }));
      setClientSecret(client_secret);
    }, [data, isLoading, error, setCustomerId, setPaymentInfoAtom]);

    const {
      trackFacebookEvent,
      trackGoogleEvent,
      trackPinterestEvent,
      trackAppLovin,
    } = useAnalytics();

    const setProcessingModalStatus = useSetAtom(processingModalStatusAtom);

    const handleProcessingStatus = useCallback(
      (status: boolean) => {
        setProcessingModalStatus(status);
      },
      [setProcessingModalStatus],
    );

    const setStripeSubscriptionActive = useSetAtom(
      stripeSubscriptionWithPersistence,
    );

    const analyticsCombinedParameters = useMemo(
      () => ({
        value: Number(currentPlanDetails.fullPriceDiscount).toFixed(2),
        currency: currentPlanDetails.currency,
      }),
      [currentPlanDetails.currency, currentPlanDetails.fullPriceDiscount],
    );

    const facebookPurchaseParameters = useMemo(
      () =>
        getFacebookPurchaseParameters({
          analyticsParameters: analyticsCombinedParameters,
          predictedLtv,
        }),
      [analyticsCombinedParameters, predictedLtv],
    );

    const facebookPurchaseCustomData = useMemo(
      () =>
        getFacebookPurchaseCustomData({
          predictedLtv,
          purchaseParameters: facebookPurchaseParameters,
        }),
      [facebookPurchaseParameters, predictedLtv],
    );

    const facebookSubscribeCustomData = useMemo(
      () =>
        getFacebookSubscribeCustomData({
          analyticsParameters: analyticsCombinedParameters,
          predictedLtv,
        }),
      [analyticsCombinedParameters, predictedLtv],
    );

    const sendAnalyticsEvents = useCallback(
      ({
        paymentMethod,
        paymentSystem,
        btSubscriptionId,
        btCustomerId,
      }: {
        paymentMethod: string;
        paymentSystem?: 'stripe' | 'braintree';
        btSubscriptionId?: string;
        btCustomerId?: string;
      }) => {
        trackFacebookEvent({
          eventName: 'Purchase',
          customData: {
            variant: getDataFromSessionStorage({
              store: 'authStore',
              key: 'variant',
            }),
            paymentMethod: paymentMethod,
            paymentSystem: paymentSystem ? paymentSystem : 'stripe',
            ...facebookPurchaseCustomData,
          },
          options: {
            ...facebookPurchaseParameters,
            email: email,
            subscription_id: undefined,
            stripe_subscription_id: planInfo.priceId,
          },
        });
        !fbPixelSept2025Feature &&
          trackFacebookEvent({
            eventName: 'Subscribe',
            customData: {
              variant: getDataFromSessionStorage({
                store: 'authStore',
                key: 'variant',
              }),
              paymentMethod: paymentMethod,
              paymentSystem: paymentSystem ? paymentSystem : 'stripe',
              ...facebookSubscribeCustomData,
            },
            options: {
              email: email,
            },
          });

        twitterEvent('tw-oehe8-oehej', {
          email: email,
          ...analyticsCombinedParameters,
        });
        trackPinterestEvent({
          eventName: 'Checkout',
          options: analyticsCombinedParameters,
        });
        trackGoogleEvent({
          eventName: 'purchase_success',
          options: {
            product_id: productId,
            price_id: planInfo.priceId,
            customer_id: customerId,
            subscription_id: paymentInfoAtom.subscriptionId,
            intro_price_id: planInfo.introOfferData?.introOfferId,
            bt_customer_id: btCustomerId ?? paymentInfoAtom.btCustomerId,
            bt_subscription_id:
              btSubscriptionId ?? paymentInfoAtom.btSubscriptionId,
            variant: getDataFromSessionStorage({
              store: 'authStore',
              key: 'variant',
            }),
            payment_system: paymentSystem ? paymentSystem : 'stripe',
            payment_type: paymentMethod ?? paymentInfoAtom.paymentMethod,
            campaign_name_manual:
              getDataFromSessionStorage({
                store: 'authStore',
                key: 'campaign',
              }) ?? 'undefined',
            email,
            ...analyticsCombinedParameters,
          },
        });

        tikTokAdvancedMatching(email);
        tikTokEvent('CompletePayment', {
          content_id: planInfo.planName,
          quantity: 1,
          ...analyticsCombinedParameters,
        });
        const appLovinPaymentSystem =
          paymentSystem || paymentInfoAtom.paymentSystem || 'stripe';
        const appLovinTransactionId =
          paymentInfoAtom.subscriptionId ||
          btSubscriptionId ||
          paymentInfoAtom.btSubscriptionId ||
          planInfo.priceId;
        trackAppLovin({
          eventName: 'purchase',
          options: createAppLovinEcommercePayload({
            currency: currentPlanDetails.currency,
            value: currentPlanDetails.fullPriceDiscount,
            itemId: planInfo.priceId,
            itemName: planInfo.planName,
            transactionId: appLovinTransactionId,
            paymentMethod: paymentMethod ?? paymentInfoAtom.paymentMethod,
            paymentSystem: appLovinPaymentSystem,
            userEmail: email,
            userId,
            variant: getDataFromSessionStorage({
              store: 'authStore',
              key: 'variant',
            }),
          }),
        });
        trackAppLovin({
          eventName: 'subscribe',
          options: createAppLovinEcommercePayload({
            currency: currentPlanDetails.currency,
            value: currentPlanDetails.fullPriceDiscount,
            itemId: planInfo.priceId,
            itemName: planInfo.planName,
            transactionId: appLovinTransactionId,
            paymentMethod: paymentMethod ?? paymentInfoAtom.paymentMethod,
            paymentSystem: appLovinPaymentSystem,
            userEmail: email,
            userId,
            variant: getDataFromSessionStorage({
              store: 'authStore',
              key: 'variant',
            }),
          }),
        });
      },
      [
        fbPixelSept2025Feature,
        analyticsCombinedParameters,
        currentPlanDetails.currency,
        currentPlanDetails.fullPriceDiscount,
        facebookPurchaseCustomData,
        facebookPurchaseParameters,
        facebookSubscribeCustomData,
        planInfo.planName,
        planInfo.introOfferData?.introOfferId,
        planInfo.priceId,
        trackFacebookEvent,
        trackGoogleEvent,
        trackPinterestEvent,
        trackAppLovin,
        paymentInfoAtom,
        email,
        userId,
        productId,
        customerId,
      ],
    );

    const { productId: academyProductId } = useGetStripeProductId({
      variant: 'Academy' as TpStripeVariants,
    });
    const { data: academiaSubscriptions } = useGetStripeSubscriptions({
      productId: academyProductId,
      wasTried: false,
    });
    const academiaPlanInfo = useMemo(
      () => academiaSubscriptions?.plans[0],
      [academiaSubscriptions],
    );

    const { mutateAsync: buySecondSubscription } = useBuySecondSubscription();
    const buyAcademy = useCallback(async () => {
      trackGoogleEvent({ eventName: 'academy_purchase_started' });
      buySecondSubscription({
        product: 'Academy',
        priceId: academiaPlanInfo!.id,
        customerId: customerId,
        currency: academiaPlanInfo!.currency,
        introOfferId: academiaPlanInfo!.introOfferData!.introOfferId!,
        introOfferDuration:
          academiaPlanInfo!.introOfferData!.introOfferDuration!,
        introOfferMustBePaid: false,
        cancelAtPeriodEnd: false,
        campaign: campaign,
        userId: String(userId),
        token: token,
        email: email,
      })
        .then((res) => {
          trackGoogleEvent({
            eventName: 'academy_purchased_successfully',
            options: {
              paymentSystem: 'stripe',
              academyIntroId: academiaPlanInfo?.introOfferData?.introOfferId,
              academySubscriptionId: res.data.subscription_id,
              value: academiaPlanInfo?.fullPriceDiscount,
              currency: academiaPlanInfo?.currency,
              priceId: academiaPlanInfo?.id,
              campaign: campaign,
              customerId: customerId,
            },
          });
        })
        .catch((e) =>
          trackGoogleEvent({
            eventName: 'academy_purchase_failed',
            options: {
              error: {
                errorData: JSON.stringify(e.response.data),
                errorName: JSON.stringify(e.name),
              },
              value: academiaPlanInfo?.fullPriceDiscount,
              currency: academiaPlanInfo?.currency,
              priceId: academiaPlanInfo?.id,
              campaign: campaign,
              customerId: customerId,
            },
          }),
        );
    }, [
      academiaPlanInfo,
      buySecondSubscription,
      trackGoogleEvent,
      customerId,
      campaign,
      email,
      token,
      userId,
    ]);

    const onSuccess = useCallback(
      async ({
        paymentMethod,
        paymentSystem,
        btSubscriptionId,
        btCustomerId,
      }: {
        paymentMethod: string;
        paymentSystem?: 'stripe' | 'braintree';
        btSubscriptionId?: string;
        btCustomerId?: string;
      }) => {
        if (onSuccessPayment) {
          await onSuccessPayment({ paymentMethod, paymentSystem });
        } else {
          isBundleFeatureEnabled && (await buyAcademy());
          sendAnalyticsEvents({
            paymentMethod,
            paymentSystem,
            btSubscriptionId,
            btCustomerId,
          });
          setStripeSubscriptionActive('subscriptionActive');
          handleProcessingStatus(false);
        }
      },
      [
        setStripeSubscriptionActive,
        handleProcessingStatus,
        sendAnalyticsEvents,
        buyAcademy,
        onSuccessPayment,
        isBundleFeatureEnabled,
      ],
    );

    const sendAddPaymentInfoEvents = useCallback(
      ({ paymentMethod }: { paymentMethod: string }) => {
        !fbPixelSept2025Feature &&
          trackFacebookEvent({
            eventName: 'AddPaymentInfo',
            customData: {
              variant: getDataFromSessionStorage({
                store: 'authStore',
                key: 'variant',
              }),
              paymentMethod: paymentMethod,
              paymentSystem: 'stripe',
            },
            options: { email },
          });
        twitterEvent('tw-oehe8-oehek', { email_address: email });
        setPaymentInfoAtom((prev) => ({
          ...prev,
          paymentMethod: paymentMethod,
          paymentSystem: 'stripe',
        }));

        trackPinterestEvent({
          eventName: 'AddToCart',
        });
        trackGoogleEvent({
          eventName: 'add_payment_info',
          options: {
            value: currentPlanDetails.fullPriceDiscount,
            payment_system: 'stripe',
            payment_method: paymentMethod,
          },
        });

        tikTokAdvancedMatching(email);
        tikTokEvent('AddPaymentInfo', { content_id: planInfo.planName });
        const appLovinPaymentPayload = createAppLovinEcommercePayload({
          currency: currentPlanDetails.currency,
          value: currentPlanDetails.fullPriceDiscount,
          itemId: planInfo.priceId,
          itemName: planInfo.planName,
          paymentMethod,
          paymentSystem: 'stripe',
          userEmail: email,
          userId,
        });
        trackAppLovin({
          eventName: 'begin_checkout',
          options: appLovinPaymentPayload,
        });
        trackAppLovin({
          eventName: 'add_payment_info',
          options: appLovinPaymentPayload,
        });
      },
      [
        fbPixelSept2025Feature,
        currentPlanDetails.currency,
        currentPlanDetails.fullPriceDiscount,
        planInfo.planName,
        planInfo.priceId,
        trackFacebookEvent,
        trackGoogleEvent,
        trackPinterestEvent,
        trackAppLovin,
        setPaymentInfoAtom,
        email,
        userId,
      ],
    );

    useEffect(() => {
      if (planInfo.priceId.length > 0 && clientSecret.length > 0) {
        trackGoogleEvent({ eventName: 'checkout_methods_loaded' });
      }
    }, [clientSecret, planInfo.priceId.length, trackGoogleEvent]);

    if (
      planInfo.priceId.length === 0 ||
      clientSecret === '' ||
      email?.length < 0
    ) {
      if (isCompactStyle) {
        if (isOnlyPaymentRequestButton) {
          return (
            <Skeleton
              height={64}
              width={327}
              borderRadius={16}
              style={{ marginTop: 16 }}
            />
          );
        }
        return <SkeletonMethodsCompact />;
      } else {
        return <SkeletonMethods />;
      }
    }

    return (
      <div
        className={classNames(
          styles.selectorContainer,
          isCompactStyle && styles.selectorContainerCompact,
        )}
      >
        {isCompactStyle ? (
          <CompactSelector
            showOnlyPaymentRequestButton={isOnlyPaymentRequestButton}
            paymentMethods={filteredPaymentOptions}
            handleProcessingStatus={handleProcessingStatus}
            sendAddPaymentInfoEvents={sendAddPaymentInfoEvents}
            onSuccess={onSuccess}
            planInfo={{
              planName: planInfo.planName,
              fullPriceDiscount: Number(currentPlanDetails.fullPriceDiscount),
              currency: currentPlanDetails.currency,
              priceId: planInfo.priceId,
              billingPeriod: planInfo.billingPeriod,
              billingPeriodUnit: planInfo.billingPeriodUnit,
              braintreePlanId: planInfo.braintreePlanId || undefined,
              braintreeMerchantId: planInfo.braintreeMerchantId || undefined,
            }}
            cancelSubscriptionAtPeriodEnd={cancelSubscriptionAtPeriodEnd}
            stripe={stripe}
            clientSecret={clientSecret}
            userEmail={email}
          />
        ) : (
          filteredPaymentOptions.map((paymentOption) => (
            <PaymentOptionTab
              paymentMethodResult={paymentMethodResult}
              onSelectPaymentMethod={handlePaymentMethodSelect}
              selected={activePaymentMethod === paymentOption.paymentMethodName}
              handleProcessingStatus={handleProcessingStatus}
              sendAddPaymentInfoEvents={sendAddPaymentInfoEvents}
              onSuccess={onSuccessPayment ?? onSuccess}
              planInfo={{
                planName: planInfo.planName,
                fullPriceDiscount: Number(currentPlanDetails.fullPriceDiscount),
                currency: currentPlanDetails.currency,
                priceId: planInfo.priceId,
                billingPeriod: planInfo.billingPeriod,
                billingPeriodUnit: planInfo.billingPeriodUnit,
                braintreePlanId: planInfo.braintreePlanId ?? undefined,
                braintreeMerchantId: planInfo.braintreeMerchantId ?? undefined,
              }}
              stripe={stripe}
              clientSecret={clientSecret}
              userEmail={email}
              key={paymentOption.paymentMethodName}
              {...paymentOption}
            />
          ))
        )}
      </div>
    );
  },
);

CheckoutSelector.displayName = 'CheckoutSelector';
